import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Text, View } from '@react-pdf/renderer';
import { Orientation, Style } from '@react-pdf/types';
import cl from 'classnames';
import { $TSFixMe } from 'src/interface';
import { uuidV4 } from 'src/helper';
import { useLanguage } from 'src/hooks';
import {
  pdfFontTranslate,
  StickyTableConfig,
  STSortOption,
} from 'src/components';
import styles from './pdfStyle.module.scss';

type Cell = {
  key: string;
  node: ReactNode;
  pdfNode: ReactNode;
};

export type PdfTableParams = {
  headers: { node: ReactNode; width: string }[];
  records: ReactNode[][];
  summaryRecord?: ReactNode[];
  tableHeight: number;
};

const PdfTableHidden: React.FC<{
  headers: Cell[];
  rows: Cell[][];
  summaryRow: Cell[] | null;
  className?: string;
  orientation?: Orientation;
  onRender: (prepParams: PdfTableParams) => void;
}> = ({ headers, rows, summaryRow, className, orientation, onRender }) => {
  const { t } = useLanguage();
  const uuid = uuidV4();
  const contentWidth = useMemo(
    () => (orientation === 'portrait' ? '539pt' : '785pt'),
    [orientation],
  );

  const widthRef = useRef<Map<number, number>>(new Map());
  const tableHeightRef = useRef<number>(0);
  const [init, setInit] = useState(false);

  useEffect(() => {
    if (init) {
      return;
    }

    setInit(true);

    const fullWidth = headers.reduce((res, cur, index) => {
      return res + (widthRef.current?.get(index) ?? 0);
    }, 0);

    const widthMap = headers.reduce((res, cur, index) => {
      return [
        ...res,
        `${(((widthRef.current?.get(index) ?? 0) * 100) / fullWidth).toFixed(3)}%`,
      ];
    }, [] as string[]);

    onRender({
      headers: headers.map(({ pdfNode }, index) => ({
        node: pdfNode,
        width: widthMap[index],
      })),
      records: rows.map((row) => row.map(({ pdfNode }) => pdfNode)),
      summaryRecord: summaryRow
        ? summaryRow.map(({ pdfNode }) => pdfNode)
        : undefined,
      tableHeight: (tableHeightRef.current ?? 0) * 1.02,
    });
  }, [headers, rows, summaryRow, onRender, init]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.hiddenWrapper}>
        <div
          className={cl(styles.table, className)}
          style={{
            gridTemplateColumns: `repeat(${headers.length}, auto)`,
            minWidth: contentWidth,
            maxWidth: contentWidth,
          }}
          ref={(node) => {
            const rect = node?.getBoundingClientRect();
            if (rect) {
              tableHeightRef.current = rect.height;
            }
          }}
        >
          {headers.map(({ key, node }, index) => (
            <div
              key={`pdf-t-h-${uuid}-${key}`}
              className={styles.headerCell}
              ref={(node) => {
                const rect = node?.getBoundingClientRect();
                if (rect) {
                  widthRef.current.set(index, rect.width);
                }
              }}
            >
              {node}
            </div>
          ))}
          {summaryRow &&
            summaryRow.map(({ key, node }) => (
              <div
                key={`pdf-t-sr-${uuid}-${key}`}
                className={cl(styles.cell, styles.summary)}
              >
                {node}
              </div>
            ))}
          {rows.map((cells, rowIndex) =>
            cells.map(({ key, node }) => (
              <div
                key={`pdf-t-row-${uuid}-${rowIndex}-${key}`}
                className={styles.cell}
              >
                {node}
              </div>
            )),
          )}
          {!rows.length && (
            <div
              className={cl(styles.cell, styles.summary)}
              style={{ gridColumn: `1 / ${headers.length + 1}` }}
            >
              {t('general.noData')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type PdfTableProps<T> = {
  isActive: boolean;
  config: StickyTableConfig<T>[];
  data: T[];
  summaryRecord?: T;
  className?: string;
  sortOption?: STSortOption | null;
  orientation?: Orientation;
  onRender: (params: PdfTableParams) => void;
};

declare const DeclaredPdfTableType: <RecordType extends object = $TSFixMe>(
  props: PdfTableProps<RecordType>,
) => React.ReactElement | null;

export const PdfTablePreparer: typeof DeclaredPdfTableType = ({
  isActive,
  config,
  data,
  summaryRecord,
  className,
  sortOption = null,
  orientation = 'portrait',
  onRender,
}) => {
  const [headers, setHeaders] = useState<Cell[] | null>(null);
  const [rows, setRows] = useState<Cell[][] | null>(null);
  const [summaryRow, setSummaryRow] = useState<Cell[] | null>(null);

  useEffect(() => {
    if (!isActive) {
      setHeaders(null);
      setRows(null);
      setSummaryRow(null);
      return;
    }

    const sortFunction = sortOption
      ? config.find(({ key }) => sortOption.key === key)?.sorter
      : null;

    const sortedData = [...data].sort((a, b) => {
      if (!sortOption || !sortFunction) {
        return 0;
      }

      return sortFunction(a, b, sortOption.sortOrder);
    });

    setHeaders(
      config.map(({ key, title }) => ({
        key,
        node: title.replaceAll('\n', ' '),
        pdfNode: title.replaceAll('\n', ' '),
      })),
    );

    if (summaryRecord && sortedData.length) {
      setSummaryRow(
        config.map(
          (
            {
              key,
              field,
              summaryRender,
              summaryPdfRender,
              printRender,
              render,
              pdfRender,
            },
            index,
          ) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            const value = summaryRecord[field];
            return {
              key,
              node:
                summaryRender?.(value, summaryRecord, index) ||
                printRender?.(value, summaryRecord, index) ||
                render?.(value, summaryRecord, index) ||
                value,
              pdfNode:
                summaryPdfRender?.(value, summaryRecord, index) ||
                pdfRender?.(value, summaryRecord, index) ||
                summaryRender?.(value, summaryRecord, index) ||
                render?.(value, summaryRecord, index) ||
                value,
            };
          },
        ),
      );
    }

    setRows(
      sortedData.map((record) =>
        config.map(({ key, field, render, printRender, pdfRender }, index) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const value = record[field];
          return {
            key,
            node:
              printRender?.(value, record, index) ||
              render?.(value, record, index) ||
              value,
            pdfNode: pdfRender?.(value, record, index) || (
              <Text
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: 10,
                }}
              >
                {render?.(value, record, index) || value}
              </Text>
            ),
          };
        }),
      ),
    );
  }, [config, data, isActive, sortOption, summaryRecord]);

  if (!headers || !rows) {
    return null;
  }

  return (
    <PdfTableHidden
      headers={headers}
      rows={rows}
      summaryRow={summaryRow}
      className={className}
      orientation={orientation}
      onRender={onRender}
    />
  );
};

const HeightCheckerHidden: React.FC<{
  onRender: (height: number) => void;
  clbHeight: number;
  orientation?: Orientation;
  children: ReactNode;
}> = ({ onRender, clbHeight, orientation, children }) => {
  const contentWidth = useMemo(
    () => (orientation === 'portrait' ? '539pt' : '785pt'),
    [orientation],
  );
  const heightRef = useRef<number>(0);

  useEffect(() => {
    onRender(heightRef.current);
  }, [onRender]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.hiddenWrapper}>
        <div
          style={{
            minWidth: contentWidth,
            maxWidth: contentWidth,
          }}
          ref={(node) => {
            heightRef.current =
              node?.getBoundingClientRect()?.height ?? clbHeight;
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const PdfHeightChecker: React.FC<{
  isActive: boolean;
  onRender: (height: number) => void;
  clbHeight: number;
  orientation?: Orientation;
  children: ReactNode;
}> = ({ isActive, onRender, clbHeight, orientation, children }) => {
  if (!isActive) {
    return null;
  }

  return (
    <HeightCheckerHidden
      onRender={onRender}
      clbHeight={clbHeight}
      orientation={orientation}
    >
      {children}
    </HeightCheckerHidden>
  );
};

export const PdfTableView: React.FC<{
  params: PdfTableParams;
  style?: Style | Style[];
  breakPage?: boolean;
}> = ({
  params: { headers, records, summaryRecord },
  style,
  breakPage = false,
}) => {
  const uuid = uuidV4();
  const { t } = useLanguage();

  return (
    <View
      style={{
        flexDirection: 'column',
        width: '100%',
        border: '1pt solid #000',
        ...(style || {}),
      }}
      break={breakPage}
    >
      <View
        style={{
          flexDirection: 'row',
          width: '100%',
          border: '1pt solid #000',
        }}
      >
        {headers.map(({ node, width }, index) => (
          <View
            break
            style={{
              width: width,
              minWidth: width,
              maxWidth: width,
              display: 'flex',
              alignItems: 'flex-start',
              textAlign: 'left',
              justifyContent: 'center',
              padding: '4.5pt 3pt',
              borderLeft: index !== 0 ? '2pt solid #000' : undefined,
            }}
            key={`pdf-table-${uuid}-header-${index}`}
          >
            <Text
              style={{
                display: 'flex',
                fontFamily: 'Inter',
                fontWeight: 600,
                ...pdfFontTranslate(10, 1.7),
              }}
            >
              {node}
            </Text>
          </View>
        ))}
      </View>
      {summaryRecord && (
        <View
          style={{
            flexDirection: 'row',
            width: '100%',
            border: '1pt solid #000',
          }}
        >
          {summaryRecord.map((node, index) => (
            <View
              break
              style={{
                width: headers[index].width,
                minWidth: headers[index].width,
                maxWidth: headers[index].width,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                padding: '3pt',
                textAlign: 'left',
                borderLeft: index !== 0 ? '2pt solid #000' : undefined,
              }}
              key={`pdf-table-${uuid}-summary-${index}`}
            >
              <Text
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: 10,
                }}
              >
                {node}
              </Text>
            </View>
          ))}
        </View>
      )}
      {records.map((row, rowIndex) => (
        <View
          key={`pdf-table-${uuid}-row-${rowIndex}`}
          style={{
            flexDirection: 'row',
            width: '100%',
            border: '1pt solid #000',
          }}
        >
          {row.map((node, index) => (
            <View
              break
              style={{
                width: headers[index].width,
                minWidth: headers[index].width,
                maxWidth: headers[index].width,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                textAlign: 'left',
                padding: '3pt',
                borderLeft: index !== 0 ? '2pt solid #000' : undefined,
              }}
              key={`pdf-table-${uuid}-cell-${rowIndex}-${index}`}
            >
              {node}
            </View>
          ))}
        </View>
      ))}
      {!records.length && (
        <View
          style={{
            flexDirection: 'row',
            width: '100%',
            border: '1pt solid #000',
          }}
        >
          <View
            break
            style={{
              width: '100%',
              minWidth: '100%',
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              padding: '3pt',
              textAlign: 'left',
            }}
          >
            <Text
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: 10,
              }}
            >
              {t('general.noData')}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};
